import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Filter, Download, Clock, Eye, Tag } from 'lucide-react'
import { api } from '../lib/api'

export default function SearchPage() {
  const [query, setQuery] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCollection, setSelectedCollection] = useState('')

  const { data: collections } = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.getCollections(),
  })

  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['search', searchQuery, selectedCollection],
    queryFn: () => api.searchObjects(searchQuery, {
      collection_id: selectedCollection || undefined,
      limit: 50
    }),
    enabled: searchQuery.length > 0
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      setSearchQuery(query.trim())
    }
  }

  const formatStixType = (type: string) => {
    return type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Search Intelligence</h1>
        <p className="text-gray-600">
          Search across all collections and threat indicators
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <form onSubmit={handleSearch}>
          <div className="flex space-x-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search indicators, malware, campaigns..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <button 
              type="submit" 
              disabled={isSearching || !query.trim()}
              className="bg-primary-600 text-white px-6 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <Search className="h-4 w-4" />
              <span>{isSearching ? 'Searching...' : 'Search'}</span>
            </button>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700">Collection:</span>
              <select
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All Collections</option>
                {collections?.collections?.map((collection: any) => (
                  <option key={collection.id} value={collection.id}>
                    {collection.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </form>
      </div>

      {searchResults ? (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Search Results ({searchResults.total || 0} found)
              </h3>
              {searchResults.results && searchResults.results.length > 0 && (
                <button className="text-primary-600 hover:text-primary-700 flex items-center space-x-2">
                  <Download className="h-4 w-4" />
                  <span>Export Results</span>
                </button>
              )}
            </div>
          </div>
          
          {searchResults.results && searchResults.results.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {searchResults.results.map((result: any, index: number) => (
                <div key={result.id || index} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Tag className="h-4 w-4 text-primary-600" />
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          {formatStixType(result.type)}
                        </span>
                        <span className="text-xs text-gray-500">
                          ID: {result.id}
                        </span>
                      </div>
                      
                      <h4 className="text-lg font-medium text-gray-900 mb-2">
                        {result.name || result.value || result.pattern || 'Unnamed Object'}
                      </h4>
                      
                      {result.description && (
                        <p className="text-gray-600 mb-3 line-clamp-2">
                          {result.description}
                        </p>
                      )}
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span>Modified: {new Date(result.modified).toLocaleDateString()}</span>
                        </div>
                        {result.labels && result.labels.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <span>Labels:</span>
                            {result.labels.slice(0, 3).map((label: string, idx: number) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                                {label}
                              </span>
                            ))}
                            {result.labels.length > 3 && (
                              <span className="text-gray-400">+{result.labels.length - 3} more</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <button className="ml-4 text-gray-400 hover:text-primary-600">
                      <Eye className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-500">
                No objects match your search query "{searchQuery}". Try adjusting your search terms.
              </p>
            </div>
          )}
        </div>
      ) : searchQuery ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Searching...</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Search Intelligence Data</h3>
          <p className="text-gray-500">
            Enter a search query to find threat indicators, malware, campaigns, and other intelligence data.
          </p>
        </div>
      )}
    </div>
  )
}